export type MailboxFolder = 'inbox' | 'sent' | 'drafts';

export interface DirectMessage {
  id: string;
  fromUserId: string;
  fromName: string;
  toUserId: string;
  toName: string;
  toEmail?: string;
  fromEmail?: string;
  subject: string;
  body: string;
  inApp: boolean;
  email: boolean;
  read: boolean;
  readAt?: number | null;
  important?: boolean;
  deletedAt?: number | null;
  draft?: boolean;
  folder?: MailboxFolder;
  source?: 'app' | 'email' | 'support_mirror' | 'resend_sync';
  provider?: 'resend' | 'app';
  providerMessageId?: string | null;
  ticketId?: string | null;
  createdAt: number;
  updatedAt?: number | null;
}

export interface SendDirectMessageInput {
  fromUserId: string;
  fromName: string;
  toUserId: string;
  toName: string;
  toEmail?: string;
  subject: string;
  body: string;
  inApp?: boolean;
  email?: boolean;
}

export interface SendDirectMessageResult {
  id: string;
  inAppDelivered: boolean;
  emailDelivered: boolean;
}

export interface MailboxUpdateInput {
  messageIds: string[];
  read?: boolean;
  important?: boolean;
  deleted?: boolean;
}

export interface SaveMailboxDraftInput {
  draftId?: string;
  toUserId?: string;
  toName?: string;
  toEmail?: string;
  subject: string;
  body: string;
  inApp?: boolean;
  email?: boolean;
}

export function createDirectMessage(
  fields: Partial<DirectMessage> & Pick<SendDirectMessageInput, 'fromUserId' | 'toUserId' | 'subject' | 'body'>,
): DirectMessage {
  return {
    id: fields.id ?? crypto.randomUUID(),
    fromUserId: fields.fromUserId,
    fromName: fields.fromName ?? '',
    toUserId: fields.toUserId,
    toName: fields.toName ?? '',
    toEmail: fields.toEmail,
    fromEmail: fields.fromEmail,
    subject: fields.subject,
    body: fields.body,
    inApp: fields.inApp ?? true,
    email: fields.email ?? false,
    read: fields.read ?? false,
    readAt: fields.readAt ?? null,
    important: fields.important ?? false,
    deletedAt: fields.deletedAt ?? null,
    draft: fields.draft ?? false,
    folder: fields.folder ?? (fields.draft ? 'drafts' : 'inbox'),
    source: fields.source ?? 'app',
    provider: fields.provider ?? 'app',
    providerMessageId: fields.providerMessageId ?? null,
    ticketId: fields.ticketId ?? null,
    createdAt: fields.createdAt ?? Date.now(),
    updatedAt: fields.updatedAt ?? null,
  };
}

/** Active (non-deleted) messages in a mailbox folder. */
export function filterMailboxMessages(
  messages: DirectMessage[],
  folder: MailboxFolder | 'important' | 'unread' = 'inbox',
): DirectMessage[] {
  const live = messages.filter((m) => !m.deletedAt);
  if (folder === 'unread') return live.filter((m) => !m.read && !m.draft && m.folder !== 'drafts');
  if (folder === 'important') return live.filter((m) => m.important && !m.draft && m.folder !== 'drafts');
  if (folder === 'drafts') return live.filter((m) => m.draft || m.folder === 'drafts');
  if (folder === 'sent') return live.filter((m) => m.folder === 'sent');
  return live.filter((m) => !m.draft && m.folder !== 'drafts' && m.folder !== 'sent');
}
